import React, { useState, useEffect, createContext, useContext, useRef, useCallback } from 'react';
import { NavLink, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useTheme } from '../../context/ThemeContext';
import { useSettings } from '../../context/SettingsContext';
import { getOfflineInspectionCount, isOnline } from '../../services/offlineService';
import { assetsApi, defectsApi, workOrdersApi, usersApi, inAppNotificationsApi } from '../../services/api';
import { trackEvent, EVENTS, setUserProperty } from '../../lib/analytics';
import { toast } from 'sonner';
import {
  DENSITY_OPTIONS, LANDING_OPTIONS,
  getGlobalDensity, setGlobalDensity,
  getLandingScreen, setLandingScreen,
} from '../../utils/navPrefs';
import { 
  LayoutDashboard, 
  Package, 
  Boxes,
  ClipboardCheck, 
  AlertTriangle, 
  Wrench, 
  Calendar,
  Users,
  Settings,
  LogOut,
  QrCode,
  Menu,
  X,
  Wifi,
  WifiOff,
  FileText,
  Bell,
  Search,
  ChevronLeft,
  ChevronRight,
  PanelLeftClose,
  PanelLeft,
  HelpCircle,
  Map,
  Database,
  User,
  Loader2,
  ArrowRight,
  BarChart3,
  Sun,
  Moon,
  Shield,
  ListChecks,
  Award,
  TrendingUp,
  Zap,
  BookOpen,
  Sparkles,
  LifeBuoy,
  SlidersHorizontal,
  Play,
  MapPin
} from 'lucide-react';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Sheet, SheetContent, SheetTrigger } from '../ui/sheet';
import { Input } from '../ui/input';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../ui/tooltip';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';

const EQUIPR_ICON = "/equipr-icon.png";

// Global Search Component
const GlobalSearch = () => {
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState({ assets: [], defects: [], workOrders: [], users: [] });
  const inputRef = useRef(null);
  const containerRef = useRef(null);
  const debounceRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Keyboard shortcut: Cmd/Ctrl + K to focus search
  useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        inputRef.current?.focus();
        setIsOpen(true);
      }
      if (e.key === 'Escape') {
        setIsOpen(false);
        inputRef.current?.blur();
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, []);

  const performSearch = useCallback(async (searchQuery) => {
    if (!searchQuery || searchQuery.length < 2) {
      setResults({ assets: [], defects: [], workOrders: [], users: [] });
      return;
    }

    setLoading(true);
    try {
      const [assetsRes, defectsRes, workOrdersRes, usersRes] = await Promise.all([
        assetsApi.getAll().then(res => res.data).catch(() => []),
        defectsApi.getAll().then(res => res.data).catch(() => []),
        workOrdersApi.getAll().then(res => res.data).catch(() => []),
        usersApi.getAll().then(res => res.data).catch(() => []),
      ]);

      const q = searchQuery.toLowerCase();

      const filteredAssets = (assetsRes || [])
        .filter(a => 
          a.name?.toLowerCase().includes(q) || 
          a.serial_number?.toLowerCase().includes(q) ||
          a.make?.toLowerCase().includes(q) ||
          a.model?.toLowerCase().includes(q)
        )
        .slice(0, 5);

      const filteredDefects = (defectsRes || [])
        .filter(d => 
          d.title?.toLowerCase().includes(q) || 
          d.description?.toLowerCase().includes(q)
        )
        .slice(0, 5);

      const filteredWorkOrders = (workOrdersRes || [])
        .filter(w => 
          w.title?.toLowerCase().includes(q) || 
          w.description?.toLowerCase().includes(q)
        )
        .slice(0, 5);

      const filteredUsers = (usersRes || [])
        .filter(u => 
          u.name?.toLowerCase().includes(q) || 
          u.email?.toLowerCase().includes(q)
        )
        .slice(0, 5);

      setResults({
        assets: filteredAssets,
        defects: filteredDefects,
        workOrders: filteredWorkOrders,
        users: filteredUsers,
      });
    } catch (error) {
      console.error('Search error:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  const handleInputChange = (e) => {
    const value = e.target.value;
    setQuery(value);
    setIsOpen(true);

    // Debounce search
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }
    debounceRef.current = setTimeout(() => {
      performSearch(value);
    }, 300);
  };

  const handleResultClick = (path) => {
    setIsOpen(false);
    setQuery('');
    navigate(path);
  };

  const totalResults = results.assets.length + results.defects.length + results.workOrders.length + results.users.length;
  const hasQuery = query.length >= 2;

  return (
    <div ref={containerRef} className="relative flex-1 max-w-lg mx-8">
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
        <Input
          ref={inputRef}
          type="text"
          placeholder="Search Guardian..."
          value={query}
          onChange={handleInputChange}
          onFocus={() => setIsOpen(true)}
          className="pl-10 pr-20 bg-secondary/50 border-border focus:bg-card"
          data-testid="global-search-input"
        />
        <kbd className="absolute right-3 top-1/2 -translate-y-1/2 hidden sm:inline-flex h-5 select-none items-center gap-1 rounded border border-border bg-muted px-1.5 font-mono text-[10px] font-medium text-muted-foreground">
          <span className="text-xs">⌘</span>K
        </kbd>
      </div>

      {/* Search Results Dropdown */}
      {isOpen && hasQuery && (
        <div className="absolute top-full left-0 right-0 mt-2 bg-card border border-border rounded-lg shadow-xl z-50 max-h-[70vh] overflow-y-auto">
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-5 h-5 animate-spin text-primary" />
              <span className="ml-2 text-sm text-muted-foreground">Searching...</span>
            </div>
          ) : totalResults === 0 ? (
            <div className="py-8 text-center">
              <p className="text-muted-foreground">No results found for "{query}"</p>
              <p className="text-xs text-muted-foreground mt-1">Try searching by name, serial number, or description</p>
            </div>
          ) : (
            <div className="py-2">
              {/* Assets */}
              {results.assets.length > 0 && (
                <div>
                  <div className="px-3 py-2 flex items-center justify-between">
                    <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Assets</span>
                    <button 
                      onClick={() => handleResultClick('/assets')}
                      className="text-xs text-primary hover:underline flex items-center gap-1"
                    >
                      View all <ArrowRight className="w-3 h-3" />
                    </button>
                  </div>
                  {results.assets.map(asset => (
                    <button
                      key={asset.id}
                      onClick={() => handleResultClick(`/assets?view=${asset.id}`)}
                      className="w-full px-3 py-2 flex items-center gap-3 hover:bg-secondary/50 transition-colors text-left"
                      data-testid={`search-result-asset-${asset.id}`}
                    >
                      <div className="w-8 h-8 rounded bg-secondary flex items-center justify-center">
                        <Package className="w-4 h-4 text-primary" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium truncate">{asset.name}</p>
                        <p className="text-xs text-muted-foreground truncate">{asset.serial_number} • {asset.make} {asset.model}</p>
                      </div>
                    </button>
                  ))}
                </div>
              )}

              {/* Defects */}
              {results.defects.length > 0 && (
                <div className={results.assets.length > 0 ? 'border-t border-border' : ''}>
                  <div className="px-3 py-2 flex items-center justify-between">
                    <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Defects</span>
                    <button 
                      onClick={() => handleResultClick('/defects')}
                      className="text-xs text-primary hover:underline flex items-center gap-1"
                    >
                      View all <ArrowRight className="w-3 h-3" />
                    </button>
                  </div>
                  {results.defects.map(defect => (
                    <button
                      key={defect.id}
                      onClick={() => handleResultClick(`/defects?view=${defect.id}`)}
                      className="w-full px-3 py-2 flex items-center gap-3 hover:bg-secondary/50 transition-colors text-left"
                      data-testid={`search-result-defect-${defect.id}`}
                    >
                      <div className="w-8 h-8 rounded bg-red-900/30 flex items-center justify-center">
                        <AlertTriangle className="w-4 h-4 text-red-400" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium truncate">{defect.title}</p>
                        <p className="text-xs text-muted-foreground truncate capitalize">{defect.severity} • {defect.status}</p>
                      </div>
                    </button>
                  ))}
                </div>
              )}

              {/* Work Orders */}
              {results.workOrders.length > 0 && (
                <div className={(results.assets.length > 0 || results.defects.length > 0) ? 'border-t border-border' : ''}>
                  <div className="px-3 py-2 flex items-center justify-between">
                    <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Work Orders</span>
                    <button 
                      onClick={() => handleResultClick('/work-orders')}
                      className="text-xs text-primary hover:underline flex items-center gap-1"
                    >
                      View all <ArrowRight className="w-3 h-3" />
                    </button>
                  </div>
                  {results.workOrders.map(wo => (
                    <button
                      key={wo.id}
                      onClick={() => handleResultClick(`/work-orders?view=${wo.id}`)}
                      className="w-full px-3 py-2 flex items-center gap-3 hover:bg-secondary/50 transition-colors text-left"
                      data-testid={`search-result-workorder-${wo.id}`}
                    >
                      <div className="w-8 h-8 rounded bg-blue-900/30 flex items-center justify-center">
                        <Wrench className="w-4 h-4 text-blue-400" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium truncate">{wo.title}</p>
                        <p className="text-xs text-muted-foreground truncate capitalize">{wo.status?.replace('_', ' ')}</p>
                      </div>
                    </button>
                  ))}
                </div>
              )}

              {/* Users */}
              {results.users.length > 0 && (
                <div className={(results.assets.length > 0 || results.defects.length > 0 || results.workOrders.length > 0) ? 'border-t border-border' : ''}>
                  <div className="px-3 py-2 flex items-center justify-between">
                    <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Users</span>
                    <button 
                      onClick={() => handleResultClick('/users')}
                      className="text-xs text-primary hover:underline flex items-center gap-1"
                    >
                      View all <ArrowRight className="w-3 h-3" />
                    </button>
                  </div>
                  {results.users.map(user => (
                    <button
                      key={user.id}
                      onClick={() => handleResultClick(`/users?view=${user.id}`)}
                      className="w-full px-3 py-2 flex items-center gap-3 hover:bg-secondary/50 transition-colors text-left"
                      data-testid={`search-result-user-${user.id}`}
                    >
                      <div className="w-8 h-8 rounded-full bg-primary flex items-center justify-center">
                        <span className="text-primary-foreground font-bold text-sm">
                          {user.name?.charAt(0).toUpperCase()}
                        </span>
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium truncate">{user.name}</p>
                        <p className="text-xs text-muted-foreground truncate">{user.email} • {user.role}</p>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// Notification Bell Component with Dropdown
const NotificationBell = () => {
  const navigate = useNavigate();
  const { formatDate } = useSettings();
  const [isOpen, setIsOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const containerRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Fetch unread count on mount and periodically
  useEffect(() => {
    fetchUnreadCount();
    const interval = setInterval(fetchUnreadCount, 60000); // Every minute
    return () => clearInterval(interval);
  }, []);

  const fetchUnreadCount = async () => {
    try {
      const response = await inAppNotificationsApi.getUnreadCount();
      setUnreadCount(response.data.unread_count || 0);
    } catch (error) {
      console.error('Failed to fetch notification count:', error);
    }
  };

  const fetchNotifications = async () => {
    setLoading(true);
    try {
      const response = await inAppNotificationsApi.getAll(false, 10);
      setNotifications(response.data);
    } catch (error) {
      console.error('Failed to fetch notifications:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleOpen = () => {
    setIsOpen(!isOpen);
    if (!isOpen) {
      grouped ? fetchGrouped() : fetchNotifications();
    }
  };

  const handleMarkAsRead = async (id, e) => {
    e.stopPropagation();
    try {
      await inAppNotificationsApi.markAsRead(id);
      setNotifications(prev => prev.map(n => n.id === id ? { ...n, is_read: true } : n));
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (error) {
      console.error('Failed to mark as read:', error);
    }
  };

  const [grouped, setGrouped] = useState(true);
  const [filter, setFilter] = useState('all'); // all | unread

  const fetchGrouped = async () => {
    setLoading(true);
    try {
      const response = await inAppNotificationsApi.getGrouped();
      setNotifications(response.data?.conditions || []);
    } catch (error) {
      console.error('Failed to fetch grouped notifications:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleMarkAllRead = async () => {
    try {
      await inAppNotificationsApi.markAllAsRead();
      setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
      setUnreadCount(0);
    } catch (error) {
      console.error('Failed to mark all as read:', error);
    }
  };

  const reload = () => { grouped ? fetchGrouped() : fetchNotifications(); };

  const handleNotificationClick = (notification) => {
    const ids = notification.ids || [notification.id];
    if (!notification.is_read) {
      ids.forEach(id => inAppNotificationsApi.markAsRead(id).catch(() => {}));
      setNotifications(prev => prev.map(n => n.id === notification.id ? { ...n, is_read: true } : n));
      setUnreadCount(prev => Math.max(0, prev - 1));
    }
    if (notification.link) navigate(notification.link);
    setIsOpen(false);
  };

  // Severity owns the left edge + icon tile tint (note 06).
  const severityMeta = (priority) => {
    switch (priority) {
      case 'critical': return { edge: '#B91C1C', tileBg: '#FDECEC', tileFg: '#B91C1C', pillBg: '#FDECEC', pillFg: '#B91C1C' };
      case 'high': return { edge: '#EA6A20', tileBg: '#FFF3E9', tileFg: '#C2560F', pillBg: '#FFF3E9', pillFg: '#C2560F' };
      default: return { edge: 'transparent', tileBg: '#F4F2ED', tileFg: '#6B6660', pillBg: '#F1EFEA', pillFg: '#6B6660' };
    }
  };

  // Icon states the record type (note 04). Match by type prefix so both the
  // new semantic types and the legacy *_critical style keys resolve.
  const iconForType = (type = '') => {
    const t = type.toLowerCase();
    if (t.includes('cert')) return Award;
    if (t.includes('defect')) return AlertTriangle;
    if (t.includes('work_order') || t.includes('workorder')) return Wrench;
    if (t.includes('inspection')) return ClipboardCheck;
    if (t.includes('maintenance')) return Wrench;
    if (t.includes('gps') || t.includes('asset') || t.includes('geofence')) return MapPin;
    return Bell;
  };

  // Every actionable row names its destination (note 07).
  const actionForType = (type = '') => {
    const t = type.toLowerCase();
    if (t.includes('cert')) return 'Open register';
    if (t.includes('defect')) return 'Raise work order';
    if (t.includes('work_order') || t.includes('workorder')) return 'Open timeline';
    if (t.includes('maintenance')) return 'Open schedule';
    if (t.includes('gps') || t.includes('asset') || t.includes('geofence')) return 'Open timeline';
    if (t.includes('inspection')) return null; // clean sweep — no action
    return null;
  };

  const formatTimeAgo = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const now = new Date();
    const seconds = Math.floor((now - date) / 1000);
    if (seconds < 60) return 'Just now';
    if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
    if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`;
    if (seconds < 604800) return `${Math.floor(seconds / 86400)}d ago`;
    return formatDate(dateString);
  };

  const shown = notifications.filter(n => filter === 'unread' ? !n.is_read : true);
  const unreadShown = notifications.filter(n => !n.is_read).length;

  return (
    <div ref={containerRef} className="relative nav-icon-wrap" data-open={isOpen}>
      <button
        className="nav-icon-btn"
        data-active={isOpen}
        onClick={handleOpen}
        data-testid="notification-bell"
        aria-label="Notifications"
      >
        <Bell className="w-[18px] h-[18px]" strokeWidth={1.9} />
        {unreadCount > 0 && (
          <span
            className="absolute w-2 h-2 rounded-full bg-[#FF4F00]"
            style={{ top: '5px', right: '5px' }}
            aria-label={`${unreadCount} unread notifications`}
          />
        )}
      </button>
      <span className="nav-tooltip">Notifications</span>

      {isOpen && (
        <div className="nav-menu-panel" style={{ right: 0, width: 400 }} data-testid="notification-panel">
          {/* Header */}
          <div className="flex items-center justify-between px-4 pt-3.5 pb-3 border-b border-[#EDEAE3]">
            <div className="flex items-baseline gap-2">
              <span className="uppercase tracking-[0.14em] text-[13px] font-bold text-[#1A1A1A]">Notifications</span>
              <span className="text-[12px] text-[#8F8A7F]" data-testid="notif-unread-count">{unreadShown} unread</span>
            </div>
            <div className="flex items-center gap-1">
              <button
                onClick={handleMarkAllRead}
                className="text-[12px] text-[#6B6660] hover:text-[#1A1A1A] px-2 py-1 rounded-md hover:bg-[#F4F2ED] transition-colors"
                data-testid="notif-mark-all-read"
              >
                Mark all read
              </button>
              <button
                onClick={() => { navigate('/settings?tab=notifications'); setIsOpen(false); }}
                className="w-7 h-7 rounded-md flex items-center justify-center text-[#8F8A7F] hover:text-[#1A1A1A] hover:bg-[#F4F2ED] transition-colors"
                title="Notification settings"
                data-testid="notif-settings-gear"
              >
                <SlidersHorizontal className="w-4 h-4" />
              </button>
            </div>
          </div>

          {/* Filter row */}
          <div className="flex items-center justify-between px-4 py-2.5 border-b border-[#EDEAE3]">
            <div className="inline-flex items-center gap-1 bg-[#F1EFEA] rounded-lg p-0.5">
              {[{ id: 'all', label: 'All', n: notifications.length }, { id: 'unread', label: 'Unread', n: unreadShown }].map(o => {
                const active = filter === o.id;
                return (
                  <button key={o.id} onClick={() => setFilter(o.id)} data-testid={`notif-filter-${o.id}`}
                    className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-md text-[12.5px] font-semibold transition-colors ${active ? 'bg-[#1A1A1A] text-white' : 'text-[#6B6660] hover:text-[#1A1A1A]'}`}>
                    {o.label}<span className={active ? 'text-white/70' : 'text-[#A39D91]'}>{o.n}</span>
                  </button>
                );
              })}
            </div>
            <button
              onClick={() => setGrouped(g => { const nv = !g; setTimeout(() => (nv ? fetchGrouped() : fetchNotifications()), 0); return nv; })}
              data-testid="notif-grouped-toggle"
              className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-md text-[12px] font-semibold border transition-colors ${grouped ? 'bg-[#FFFBEA] border-[#EFE0A0] text-[#8A6D00]' : 'bg-white border-[#DAD5CA] text-[#6B6660] hover:text-[#1A1A1A]'}`}
            >
              <ListChecks className="w-3.5 h-3.5" />{grouped ? 'Grouped' : 'By event'}
            </button>
          </div>

          {/* List */}
          <div className="max-h-[420px] overflow-y-auto">
            {loading ? (
              <div className="flex items-center justify-center py-10"><Loader2 className="w-5 h-5 animate-spin text-[#B5AFA3]" /></div>
            ) : shown.length === 0 ? (
              <div className="py-12 text-center">
                <Bell className="w-9 h-9 text-[#C9C3B6] mx-auto mb-2" />
                <p className="text-[#6B6660] text-sm font-medium">You're all caught up</p>
                <p className="text-[12px] text-[#A39D91] mt-0.5">Nothing needs your attention right now.</p>
              </div>
            ) : (
              shown.map(n => {
                const sev = severityMeta(n.priority);
                const Icon = iconForType(n.type);
                const action = n.link ? actionForType(n.type) : null;
                const occ = n.occurrence_count || 1;
                return (
                  <div
                    key={n.id}
                    onClick={() => handleNotificationClick(n)}
                    data-testid={`notif-row-${n.id}`}
                    className="relative flex items-start gap-3 px-4 py-3 cursor-pointer transition-colors border-b border-[#F2F0EB] last:border-0 hover:bg-[#FBFAF7]"
                    style={{ background: !n.is_read ? '#FFFCEF' : undefined }}
                  >
                    <span className="absolute left-0 top-0 bottom-0 w-[3px]" style={{ background: sev.edge }} />
                    <span className="flex items-center justify-center shrink-0 mt-0.5" style={{ width: 30, height: 30, borderRadius: 8, background: sev.tileBg, color: sev.tileFg }}>
                      <Icon className="w-4 h-4" />
                    </span>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start gap-2">
                        <p className={`text-[13.5px] leading-snug flex-1 ${!n.is_read ? 'font-semibold text-[#1A1A1A]' : 'font-medium text-[#3D3A35]'}`}>{n.title}</p>
                        {occ > 1 && (
                          <span className="font-mono text-[11px] px-1.5 py-0.5 rounded-md shrink-0" style={{ background: sev.pillBg, color: sev.pillFg }} data-testid={`notif-count-${n.id}`}>×{occ}</span>
                        )}
                        {!n.is_read && <span className="w-[7px] h-[7px] rounded-full bg-[#FFD108] shrink-0 mt-1.5" />}
                      </div>
                      {n.message && <p className="text-[12px] text-[#6B6660] mt-0.5 line-clamp-2">{n.message}</p>}
                      <div className="flex items-center gap-2 mt-1">
                        <span className="text-[11px] text-[#A39D91]">{formatTimeAgo(n.last_raised_at || n.created_at)}</span>
                        {action && (
                          <button
                            onClick={(e) => { e.stopPropagation(); if (n.link) navigate(n.link); setIsOpen(false); }}
                            className="text-[11.5px] font-semibold text-[#8A6D00] hover:text-[#6B5500]"
                            data-testid={`notif-action-${n.id}`}
                          >
                            {action}
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })
            )}
          </div>

          {/* Footer */}
          <button
            onClick={() => { navigate('/notifications'); setIsOpen(false); }}
            className="w-full flex items-center justify-between px-4 py-3 border-t border-[#EDEAE3] text-[13px] font-semibold text-[#1A1A1A] hover:bg-[#FBFAF7] transition-colors"
            data-testid="notif-view-all"
          >
            View all notifications
            <ChevronRight className="w-4 h-4 text-[#B5AFA3]" />
          </button>
        </div>
      )}
    </div>
  );
};

// Create sidebar context
const SidebarContext = createContext();

export const useSidebar = () => {
  const context = useContext(SidebarContext);
  if (!context) {
    return { collapsed: false, setCollapsed: () => {} };
  }
  return context;
};

const Logo = ({ collapsed = false }) => (
  <div className={`flex items-center ${collapsed ? 'justify-center' : 'gap-3'}`}>
    <img src={EQUIPR_ICON} alt="EQUIPR" className={`rounded-lg transition-all duration-300 ${collapsed ? 'w-9 h-9' : 'w-10 h-10'}`} />
    {!collapsed && (
      <div>
        <h1 className="equipr-logo text-xl font-bold tracking-tight">
          E<span className="q-accent">Q</span>UIPR
        </h1>
        <p className="text-xs text-muted-foreground tracking-wider uppercase">Guardian</p>
      </div>
    )}
  </div>
);

const NavItem = ({ to, icon: Icon, label, onClick, badge, badgeVariant = 'neutral', collapsed }) => {
  // Fix 7 count styling: alert (Defects) = orange; neutral (WO/Certs) = volt.
  const badgeStyle = badgeVariant === 'alert'
    ? { color: '#FF7A3D', background: '#3A1405', border: '1px solid #5A2408' }
    : { color: '#FFD108', background: '#3A3006', border: '1px solid #574800' };
  const dotClass = badgeVariant === 'alert' ? 'bg-[#FF7A3D]' : 'bg-[#FFD108]';
  const content = (
    <NavLink
      to={to}
      onClick={onClick}
      className={({ isActive }) => 
        `sidebar-link ${isActive ? 'active' : ''} ${collapsed ? 'sidebar-link-collapsed' : ''}`
      }
      style={collapsed ? { 
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        padding: '12px 0',
        margin: '2px 14px',
        borderRadius: '8px',
        width: 'calc(100% - 28px)'
      } : {}}
    >
      <Icon className="w-5 h-5 flex-shrink-0" />
      {!collapsed && <span className="text-nav uppercase font-medium flex-1">{label}</span>}
      {!collapsed && badge != null && badge !== 0 && (
        <span className="font-mono rounded-[4px]" style={{ ...badgeStyle, fontSize: 10, fontWeight: 500, padding: '1px 6px' }}>
          {badge}
        </span>
      )}
      {collapsed && badge != null && badge !== 0 && (
        <span className={`absolute top-1 right-1 w-2 h-2 rounded-full ${dotClass}`}></span>
      )}
    </NavLink>
  );

  if (collapsed) {
    return (
      <TooltipProvider delayDuration={0}>
        <Tooltip>
          <TooltipTrigger asChild>
            {content}
          </TooltipTrigger>
          <TooltipContent side="right" className="bg-card border-border">
            <p className="uppercase tracking-wide text-sm">{label}</p>
            {badge != null && badge !== 0 && (
              <span className="ml-2 font-mono rounded-[4px]" style={{ ...badgeStyle, fontSize: 10, fontWeight: 500, padding: '1px 6px' }}>{badge}</span>
            )}
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  }

  return content;
};

// Sidebar group label (Fix 7)
const SidebarGroupLabel = ({ children, className = '' }) => (
  <div
    className={`px-[18px] pb-[7px] text-[9.5px] font-bold uppercase tracking-[0.16em] text-[#6E6E6E] ${className}`}
  >
    {children}
  </div>
);


const SidebarContent = ({ onClose, collapsed = false, setCollapsed }) => {
  const { user, logout, isAdmin, isFleetManager, isOfficeUser, isMechanic } = useAuth();
  const navigate = useNavigate();
  const [online, setOnline] = useState(isOnline());
  const [offlineCount, setOfflineCount] = useState(0);
  const [plan, setPlan] = useState(null);
  const [navCounts, setNavCounts] = useState({ defects: 0, workOrders: 0 });

  useEffect(() => {
    // Fix 7: live nav counts (open defects + due work orders) from dashboard.
    const fetchCounts = async () => {
      try {
        const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/dashboard`, {
          headers: { 'Authorization': `Bearer ${localStorage.getItem('equipr_token')}` }
        });
        if (res.ok) {
          const d = await res.json();
          setNavCounts({ defects: d.open_defects || 0, workOrders: d.work_orders_due || 0 });
        }
      } catch {}
    };
    fetchCounts();
  }, []);

  useEffect(() => {
    // Fetch tenant plan
    const fetchPlan = async () => {
      try {
        const { tenantSettingsApi } = await import('../../services/api');
        const res = await tenantSettingsApi.getPublicQR(); // reuse existing endpoint pattern
        // Use dedicated plan endpoint
        const planRes = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/tenant-settings/plan`, {
          headers: { 'Authorization': `Bearer ${localStorage.getItem('equipr_token')}` }
        });
        if (planRes.ok) {
          const data = await planRes.json();
          setPlan(data.plan);
          setUserProperty('plan', data.plan);
        }
      } catch {}
    };
    fetchPlan();
  }, []);

  useEffect(() => {
    const handleOnline = () => setOnline(true);
    const handleOffline = () => setOnline(false);
    
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    
    // Check offline inspection count
    const checkOffline = async () => {
      const count = await getOfflineInspectionCount();
      setOfflineCount(count);
    };
    checkOffline();
    const interval = setInterval(checkOffline, 30000);
    
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
      clearInterval(interval);
    };
  }, []);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const handleClick = () => {
    if (onClose) onClose();
  };

  return (
    <div className="flex flex-col h-full">
      {/* Navigation Items - grouped (Fix 7) */}
      <nav className={`flex-1 overflow-y-auto ${collapsed ? 'py-2' : 'py-4'}`}>
        {!collapsed && <SidebarGroupLabel>Operate</SidebarGroupLabel>}
        <NavItem to="/dashboard" icon={LayoutDashboard} label="Dashboard" onClick={handleClick} collapsed={collapsed} />
        <NavItem to="/scanner" icon={QrCode} label="QR Scanner" onClick={handleClick} collapsed={collapsed} />
        <NavItem to="/inspections" icon={ClipboardCheck} label="Inspections" onClick={handleClick} badge={offlineCount > 0 ? offlineCount : null} collapsed={collapsed} />
        <NavItem to="/defects" icon={AlertTriangle} label="Defects" onClick={handleClick} badge={navCounts.defects} badgeVariant="alert" collapsed={collapsed} />
        <NavItem to="/work-orders" icon={Wrench} label="Work Orders" onClick={handleClick} badge={navCounts.workOrders} badgeVariant="neutral" collapsed={collapsed} />
        <NavItem to="/maintenance" icon={Calendar} label="Maintenance" onClick={handleClick} collapsed={collapsed} />

        {!collapsed && <SidebarGroupLabel className="mt-4">Fleet</SidebarGroupLabel>}
        {collapsed && <div className="my-3 mx-3"><div className="border-t border-border" /></div>}
        <NavItem to="/assets" icon={Package} label="Assets" onClick={handleClick} collapsed={collapsed} />
        <NavItem to="/fleet-map" icon={Map} label="Fleet Map" onClick={handleClick} collapsed={collapsed} />
        {isOfficeUser() && (
          <NavItem to="/inventory" icon={Boxes} label="Inventory" onClick={handleClick} collapsed={collapsed} />
        )}
        {isOfficeUser() && (
          <>
            <NavItem to="/documents" icon={FileText} label="Documents" onClick={handleClick} collapsed={collapsed} />
            <NavItem to="/certificates" icon={Award} label="Certificates" onClick={handleClick} collapsed={collapsed} />
            <NavItem to="/compliance" icon={Shield} label="Compliance" onClick={handleClick} collapsed={collapsed} />
          </>
        )}

        {(isAdmin() || isFleetManager() || isMechanic() || isOfficeUser()) && (
          <>
            {!collapsed && <SidebarGroupLabel className="mt-4">Analyse</SidebarGroupLabel>}
            {collapsed && <div className="my-3 mx-3"><div className="border-t border-border" /></div>}
            {(isAdmin() || isFleetManager() || isMechanic()) && (
              <NavItem to="/reports" icon={BarChart3} label="Reports" onClick={handleClick} collapsed={collapsed} />
            )}
            {isOfficeUser() && (
              <NavItem to="/insights" icon={TrendingUp} label="Insights" onClick={handleClick} collapsed={collapsed} />
            )}
          </>
        )}

        {isOfficeUser() && (
          <>
            {!collapsed && <SidebarGroupLabel className="mt-4">Administer</SidebarGroupLabel>}
            {collapsed && <div className="my-3 mx-3"><div className="border-t border-border" /></div>}
            {isAdmin() && (
              <NavItem to="/users" icon={Users} label="Users" onClick={handleClick} collapsed={collapsed} />
            )}
            <NavItem to="/templates" icon={ClipboardCheck} label="Templates" onClick={handleClick} collapsed={collapsed} />
            <NavItem to="/task-library" icon={ListChecks} label="Task Library" onClick={handleClick} collapsed={collapsed} />
            <NavItem to="/data-management" icon={Database} label="Data Management" onClick={handleClick} collapsed={collapsed} />
            <NavItem to="/notifications" icon={Bell} label="Notifications" onClick={handleClick} collapsed={collapsed} />
            {isAdmin() && (
              <NavItem to="/settings" icon={Settings} label="Settings" onClick={handleClick} collapsed={collapsed} />
            )}
          </>
        )}
      </nav>

      {/* Plan Badge + Upgrade CTA (Free plan only) */}
      {plan === 'free' && (
        <div className={`border-t border-border ${collapsed ? 'p-2' : 'px-4 py-3'}`}>
          {collapsed ? (
            <TooltipProvider delayDuration={0}>
              <Tooltip>
                <TooltipTrigger asChild>
                  <button
                    onClick={() => {
                      trackEvent(EVENTS.UPGRADE_CLICKED, { location: 'sidebar_collapsed' });
                      navigate('/pricing');
                    }}
                    className="w-10 h-10 mx-auto flex items-center justify-center rounded-lg bg-[#FFD108]/10 text-[#FFD108] hover:bg-[#FFD108]/20 transition-colors"
                    data-testid="upgrade-btn-collapsed"
                  >
                    <Zap className="w-4 h-4" />
                  </button>
                </TooltipTrigger>
                <TooltipContent side="right">Upgrade to Professional</TooltipContent>
              </Tooltip>
            </TooltipProvider>
          ) : (
            <div className="space-y-2" data-testid="plan-badge-section">
              <div className="flex items-center gap-2">
                <span className="text-[10px] font-bold uppercase tracking-widest bg-[#FFD108]/20 text-[#FFD108] px-2 py-0.5 rounded">Free</span>
                <span className="text-xs text-muted-foreground">Plan</span>
              </div>
              <button
                onClick={() => {
                  trackEvent(EVENTS.UPGRADE_CLICKED, { location: 'sidebar' });
                  navigate('/pricing');
                }}
                className="w-full flex items-center justify-center gap-2 px-3 py-2 rounded-lg bg-[#FFD108] text-black text-xs font-semibold uppercase tracking-wide hover:bg-[#E5B93D] transition-colors"
                data-testid="upgrade-btn"
              >
                <Zap className="w-3.5 h-3.5" />
                Upgrade to Professional
              </button>
              <p className="text-[10px] text-muted-foreground text-center">$39/office user/month</p>
            </div>
          )}
        </div>
      )}
      
      {/* Collapse Toggle Button - Always at bottom */}
      {setCollapsed && (
        <div className={`mt-auto border-t border-border ${collapsed ? 'p-3' : 'p-4'}`}>
          <TooltipProvider delayDuration={0}>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button 
                  variant="ghost" 
                  className={`w-full text-muted-foreground hover:text-foreground hover:bg-secondary ${collapsed ? 'h-10 w-10 p-0 mx-auto flex items-center justify-center' : 'justify-start h-10'}`}
                  onClick={() => setCollapsed(!collapsed)}
                  data-testid="collapse-sidebar-btn"
                >
                  {collapsed ? (
                    <PanelLeft className="w-5 h-5" />
                  ) : (
                    <>
                      <PanelLeftClose className="w-5 h-5 mr-3" />
                      <span className="uppercase tracking-wide font-medium text-sm">Collapse</span>
                    </>
                  )}
                </Button>
              </TooltipTrigger>
              {collapsed && (
                <TooltipContent side="right" className="bg-card border-border">
                  <p className="text-sm">Expand Sidebar</p>
                </TooltipContent>
              )}
            </Tooltip>
          </TooltipProvider>
        </div>
      )}
    </div>
  );
};

export const Sidebar = ({ collapsed, setCollapsed }) => {
  return (
    <aside className={`hidden lg:block sidebar transition-all duration-300 ${collapsed ? 'sidebar-collapsed' : ''}`}>
      <SidebarContent collapsed={collapsed} setCollapsed={setCollapsed} />
    </aside>
  );
};

// Top Navigation Bar Component - Full width with EQUIPR branding on left
// ── Top-bar icon menu primitives (dark bar, light panels) ──────────────
const useClickOutside = (onClose) => {
  const ref = useRef(null);
  useEffect(() => {
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) onClose(); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, [onClose]);
  return ref;
};

// A 34px icon button on the dark bar with a dark tooltip below it. The tooltip
// is suppressed while the panel is open (notes 01, 02, 16).
const NavIconMenu = ({ icon: Icon, label, testId, panelTestId, panelWidth = 328, children }) => {
  const [open, setOpen] = useState(false);
  const ref = useClickOutside(() => setOpen(false));
  return (
    <div ref={ref} className="relative nav-icon-wrap" data-open={open}>
      <button className="nav-icon-btn" data-active={open} onClick={() => setOpen(o => !o)} data-testid={testId} aria-label={label}>
        <Icon className="w-[18px] h-[18px]" strokeWidth={1.9} />
      </button>
      <span className="nav-tooltip">{label}</span>
      {open && (
        <div className="nav-menu-panel" style={{ right: 0, width: panelWidth }} data-testid={panelTestId}>
          {children(() => setOpen(false))}
        </div>
      )}
    </div>
  );
};

const HelpMenu = ({ navigate }) => {
  const WALKTHROUGHS = [
    { label: 'Asset registry tour', time: '0:38', to: '/help' },
    { label: 'Reporting a defect', time: '0:42', to: '/help' },
    { label: 'Raising a work order', time: '0:45', to: '/help' },
  ];
  return (
    <NavIconMenu icon={HelpCircle} label="Help & support" testId="help-menu-trigger" panelTestId="help-menu-popover">
      {(close) => (
        <div>
          <div className="px-4 pt-3.5 pb-2.5 border-b border-[#EDEAE3]">
            <span className="uppercase tracking-[0.14em] text-[12px] font-bold text-[#1A1A1A]">Help &amp; Support</span>
          </div>
          <div className="px-3 py-2 border-b border-[#EDEAE3]">
            <p className="uppercase tracking-[0.12em] text-[10.5px] font-bold text-[#8F8A7F] px-1 pb-1">Watch a walkthrough</p>
            {WALKTHROUGHS.map((w, i) => (
              <button key={i} onClick={() => { navigate(w.to); close(); }} data-testid={`help-walkthrough-${i}`}
                className="w-full flex items-center gap-3 px-1.5 h-10 rounded-md hover:bg-[#FBFAF7] transition-colors text-left">
                <span className="flex items-center justify-center shrink-0" style={{ width: 26, height: 26, borderRadius: 7, background: '#FFF8D6', border: '1px solid #EFE0A0' }}>
                  <Play className="w-3 h-3 text-[#8A6D00]" fill="#8A6D00" />
                </span>
                <span className="flex-1 text-[13.5px] font-semibold text-[#1A1A1A] truncate">{w.label}</span>
                <span className="font-mono text-[11px] text-[#A39D91]">{w.time}</span>
              </button>
            ))}
            <button onClick={() => { navigate('/help'); close(); }} className="w-full flex items-center gap-2 px-1.5 h-9 rounded-md hover:bg-[#FBFAF7] text-left" data-testid="help-all-walkthroughs">
              <span className="text-[12.5px] font-semibold text-[#3D3A35]">All 9 walkthroughs</span>
              <ChevronRight className="w-3.5 h-3.5 text-[#B5AFA3]" />
            </button>
          </div>
          <div className="px-3 py-2">
            {[
              { icon: BookOpen, title: 'Help centre', sub: 'Browse all articles', onClick: () => { navigate('/help'); close(); }, testId: 'help-menu-browse' },
              { icon: Sparkles, title: 'Ask Guardian AI', sub: 'Instant answers from docs', onClick: () => { navigate('/help/ai'); close(); }, testId: 'help-menu-ai' },
              { icon: LifeBuoy, title: 'Contact support', sub: 'Email our team', onClick: () => { window.dispatchEvent(new CustomEvent('guardian:open-support')); close(); }, testId: 'help-menu-contact' },
            ].map((r, i) => (
              <button key={i} onClick={r.onClick} data-testid={r.testId}
                className="w-full flex items-center gap-3 px-1.5 h-10 rounded-md hover:bg-[#FBFAF7] transition-colors text-left">
                <span className="flex items-center justify-center shrink-0" style={{ width: 26, height: 26, borderRadius: 7, background: '#F4F2ED', border: '1px solid #E6E2D9' }}>
                  <r.icon className="w-3.5 h-3.5 text-[#6B6660]" />
                </span>
                <div className="min-w-0">
                  <p className="text-[13px] font-semibold text-[#1A1A1A] leading-tight">{r.title}</p>
                  <p className="text-[11.5px] text-[#8F8A7F] leading-tight">{r.sub}</p>
                </div>
              </button>
            ))}
          </div>
        </div>
      )}
    </NavIconMenu>
  );
};

const SegRow = ({ options, value, onChange, testId }) => (
  <div className="inline-flex items-center gap-1 bg-[#F1EFEA] rounded-lg p-0.5 w-full" data-testid={testId}>
    {options.map(o => {
      const active = value === o.id;
      return (
        <button key={o.id} onClick={() => onChange(o.id)} data-testid={`${testId}-${o.id}`}
          className={`flex-1 px-2 py-1.5 rounded-md text-[12.5px] font-semibold transition-colors ${active ? 'bg-white text-[#1A1A1A] shadow-sm' : 'text-[#6B6660] hover:text-[#1A1A1A]'}`}>
          {o.label}
        </button>
      );
    })}
  </div>
);

const QuickSettingsMenu = ({ navigate }) => {
  const [density, setDensity] = useState(getGlobalDensity());
  const [landing, setLanding] = useState(getLandingScreen());
  const onDensity = (id) => { setDensity(id); setGlobalDensity(id); toast.success('Density preference saved'); };
  const onLanding = (id) => { setLanding(id); setLandingScreen(id); toast.success('Landing screen saved'); };
  return (
    <NavIconMenu icon={SlidersHorizontal} label="Quick settings" testId="settings-btn" panelTestId="quick-settings-popover" panelWidth={300}>
      {(close) => (
        <div>
          <div className="px-4 pt-3.5 pb-2.5 border-b border-[#EDEAE3]">
            <span className="uppercase tracking-[0.14em] text-[12px] font-bold text-[#1A1A1A]">Quick Settings</span>
          </div>
          <div className="px-4 py-3.5 space-y-4">
            <div>
              <p className="uppercase tracking-[0.12em] text-[10.5px] font-bold text-[#8F8A7F] mb-1.5">Table density</p>
              <SegRow options={DENSITY_OPTIONS} value={density} onChange={onDensity} testId="qs-density" />
            </div>
            <div>
              <p className="uppercase tracking-[0.12em] text-[10.5px] font-bold text-[#8F8A7F] mb-1.5">Default landing screen</p>
              <SegRow options={LANDING_OPTIONS} value={landing} onChange={onLanding} testId="qs-landing" />
            </div>
          </div>
          <button onClick={() => { navigate('/settings'); close(); }} data-testid="qs-all-settings"
            className="w-full flex items-center justify-between px-4 py-3 border-t border-[#EDEAE3] text-[13px] font-semibold text-[#1A1A1A] hover:bg-[#FBFAF7] transition-colors">
            All settings
            <ChevronRight className="w-4 h-4 text-[#B5AFA3]" />
          </button>
        </div>
      )}
    </NavIconMenu>
  );
};

const ROLE_OVERLINE = { admin: 'ADMIN', fleet_manager: 'FLEET MANAGER', mechanic: 'MECHANIC' };

const UserMenu = ({ user, navigate, onLogout }) => {
  const [open, setOpen] = useState(false);
  const ref = useClickOutside(() => setOpen(false));
  const initial = (user?.name || user?.email || '?').charAt(0).toUpperCase();
  return (
    <div ref={ref} className="relative">
      <button
        onClick={() => setOpen(o => !o)}
        data-testid="user-menu-btn"
        data-open={open}
        className="flex items-center gap-2.5 pl-1 pr-2 py-1 rounded-lg transition-colors hover:bg-[#1E1E1E] data-[open=true]:bg-[#1E1E1E]"
      >
        <span className="w-8 h-8 rounded-full bg-[#FFD108] flex items-center justify-center shrink-0">
          <span className="text-black font-bold text-sm">{initial}</span>
        </span>
        <span className="hidden xl:flex flex-col items-start leading-tight">
          <span className="text-[13px] font-semibold text-[#F5F3EE]">{user?.name}</span>
          <span className="text-[10px] font-bold tracking-[0.14em] text-[#8A8A8A]">{ROLE_OVERLINE[user?.role] || (user?.role || '').toUpperCase()}</span>
        </span>
        <ChevronRight className="w-4 h-4 text-[#8A8A8A] rotate-90" />
      </button>
      {open && (
        <div className="nav-menu-panel" style={{ right: 0, width: 288 }} data-testid="user-menu-panel">
          <div className="px-4 pt-4 pb-3.5">
            <div className="flex items-center gap-3">
              <span className="w-11 h-11 rounded-full bg-[#FFD108] flex items-center justify-center shrink-0">
                <span className="text-black font-extrabold text-lg">{initial}</span>
              </span>
              <div className="min-w-0">
                <p className="text-[15px] font-bold text-[#1A1A1A] truncate">{user?.name}</p>
                <p className="text-[12.5px] text-[#8F8A7F] truncate">{user?.email}</p>
              </div>
            </div>
            <div className="flex items-center gap-2 mt-3">
              <span className="uppercase text-[10px] font-bold tracking-[0.12em] px-2 py-1 rounded-md" style={{ background: '#F4F2ED', color: '#3D3A35', border: '1px solid #E6E2D9' }}>
                {ROLE_OVERLINE[user?.role] || (user?.role || '').toUpperCase()}
              </span>
              {user?.client_id && <span className="font-mono text-[11px] text-[#A39D91]">{user.client_id}</span>}
            </div>
          </div>
          <button onClick={() => { navigate('/profile'); setOpen(false); }} data-testid="menu-profile-link"
            className="w-full flex items-center justify-between px-4 h-12 border-t border-[#EDEAE3] hover:bg-[#FBFAF7] transition-colors text-left">
            <span className="flex items-center gap-3 text-[13.5px] font-semibold text-[#1A1A1A]"><User className="w-4 h-4 text-[#6B6660]" />Profile</span>
            <ChevronRight className="w-4 h-4 text-[#B5AFA3]" />
          </button>
          <button onClick={() => { onLogout(); }} data-testid="menu-logout"
            className="w-full flex items-center gap-3 px-4 h-12 border-t border-[#EDEAE3] text-[13.5px] font-semibold transition-colors"
            style={{ color: '#3D3A35' }}
            onMouseEnter={(e) => (e.currentTarget.style.background = '#F4F2ED')}
            onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}>
            <LogOut className="w-4 h-4 text-[#6B6660]" />Log out
          </button>
        </div>
      )}
    </div>
  );
};


export const TopNav = ({ collapsed, setCollapsed }) => {
  const { user, logout } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const [online, setOnline] = useState(isOnline());

  useEffect(() => {
    const handleOnline = () => setOnline(true);
    const handleOffline = () => setOnline(false);
    
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  return (
    <header className="top-nav-full hidden lg:flex" data-testid="top-nav">
      {/* Left section - EQUIPR Icon + Company name (with Client ID fallback) */}
      <div className="flex items-center gap-3" data-testid="topnav-brand">
        <img src={EQUIPR_ICON} alt="EQUIPR" className="w-9 h-9 rounded-lg" />
        {user?.company_name ? (
          <div className="flex items-center gap-2">
            <span
              className="font-heading font-bold text-sm tracking-tight max-w-[220px] truncate"
              title={user.company_name}
              data-testid="topnav-company-name"
            >
              {user.company_name}
            </span>
            {user?.client_id && (
              <span
                className="font-mono text-[10px] text-muted-foreground hidden xl:inline"
                data-testid="topnav-client-id"
              >
                · {user.client_id}
              </span>
            )}
          </div>
        ) : user?.client_id ? (
          <Badge
            variant="outline"
            className="font-mono text-sm px-3 py-1 border-border"
            data-testid="topnav-client-id"
          >
            {user.client_id}
          </Badge>
        ) : null}
      </div>

      {/* Center section - Global Search */}
      <GlobalSearch />

      {/* Right section - Actions & User */}
      <div className="flex items-center gap-1.5">
        <HelpMenu navigate={navigate} />
        <NotificationBell />
        <QuickSettingsMenu navigate={navigate} />
        <UserMenu user={user} navigate={navigate} onLogout={handleLogout} />
      </div>
    </header>
  );
};

export const MobileNav = () => {
  const [open, setOpen] = React.useState(false);
  
  return (
    <div className="lg:hidden">
      <header className="fixed top-0 left-0 right-0 h-16 bg-card border-b border-border flex items-center justify-between px-4 z-50">
        <Logo />
        <Sheet open={open} onOpenChange={setOpen}>
          <SheetTrigger asChild>
            <Button variant="ghost" size="icon" data-testid="mobile-menu-btn">
              <Menu className="w-6 h-6" />
            </Button>
          </SheetTrigger>
          <SheetContent side="left" className="p-0 w-72 bg-card">
            <SidebarContent onClose={() => setOpen(false)} />
          </SheetContent>
        </Sheet>
      </header>
      <div className="h-16"></div>
    </div>
  );
};

export const Layout = ({ children }) => {
  const [collapsed, setCollapsed] = useState(false);
  const { isLight } = useTheme();
  
  // Persist collapse state in localStorage
  useEffect(() => {
    const saved = localStorage.getItem('sidebar-collapsed');
    if (saved !== null) {
      setCollapsed(JSON.parse(saved));
    }
  }, []);

  useEffect(() => {
    localStorage.setItem('sidebar-collapsed', JSON.stringify(collapsed));
  }, [collapsed]);

  // Light mode content styles (Fix 1: warm ground, let .theme-light supply)
  const contentStyle = isLight ? {
    backgroundColor: '#EDEAE3',
    color: '#1A1A1A',
  } : {};

  return (
    <SidebarContext.Provider value={{ collapsed, setCollapsed }}>
      <div className="min-h-screen bg-[hsl(0,0%,7%)]">
        {/* Full-width Top Navigation - Fixed at top, always dark */}
        <TopNav collapsed={collapsed} setCollapsed={setCollapsed} />
        
        {/* Mobile Nav for small screens */}
        <MobileNav />
        
        {/* Main content area with sidebar - pushed down by top nav height */}
        <div className="flex pt-16">
          {/* Sidebar - always dark theme */}
          <Sidebar collapsed={collapsed} setCollapsed={setCollapsed} />
          
          {/* Main content - applies theme */}
          <main 
            className={`flex-1 transition-all duration-300 ${collapsed ? 'lg:ml-[72px]' : 'lg:ml-[260px]'}`}
            style={contentStyle}
          >
            <div className={`p-4 lg:p-6 min-h-[calc(100vh-4rem)] ${isLight ? 'theme-light light-content' : ''}`}>
              {children}
            </div>
          </main>
        </div>
      </div>
    </SidebarContext.Provider>
  );
};
